import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { OrganizationWithHierarchy } from "@/graphql/__generated__/graphql";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";

/**
 * Hierarchy sheet component that renders a Sheet for organization hierarchy
 * @param org - OrganizationWithHierarchy object containing children data
 */
export function HierarchySheetComponent({ org }: { org: OrganizationWithHierarchy }) {
  const { t } = useTranslation(["translation"]);

  // Children is now a proper JSON array, no parsing needed
  const children = (org.children as unknown as OrganizationWithHierarchy[]) || [];
  const count = children.length;

  if (!count) return null;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="">
          {t("adminorganization.viewhierarchy")}
        </Button>
      </SheetTrigger>

      <SheetContent className="w-[80vw] sm:w-[75vw] md:w-[65vw] lg:w-[55vw] xl:w-[40vw] max-w-none sm:max-w-none flex flex-col h-full">
        <SheetHeader>
          <SheetTitle className="text-lg">{org.name}</SheetTitle>
          <SheetDescription>
            {t("adminorganization.sheetdescription")}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="px-4 flex-1 min-h-0">
          <div className="w-full">
            <div className="pb-20">
              <HierarchyAccordion nodes={children} t={t} />
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Hierarchy accordion component that renders a nested accordion for organization hierarchy
 * @param nodes - Array of organization objects
 * @param t - Translation function
 * @returns
 */
function HierarchyAccordion({ nodes, t }: { nodes: OrganizationWithHierarchy[], t: TFunction }) {
  const defaultOpen = nodes.map((n) => String(n.id));

  return (
    <Accordion type="multiple" defaultValue={defaultOpen} className="w-full flex flex-col gap-2 min-w-30">
      {nodes.map((node) => {
        const children = (node.children as unknown as OrganizationWithHierarchy[]) || [];
        const hasChildren = children.length > 0;

        if (!hasChildren) {
          return (
            <div key={node.id} className="rounded-lg border bg-card p-3">
              <div className="flex w-full items-start justify-between gap-2">
                <div className="flex-1 text-left">
                  <div className="text-sm font-medium text-foreground">
                    {node.name}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {`${node.shortName ?? ""} • ${node.province ?? ""}`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {Boolean(node.isOperational) && <Badge>{t("adminorganization.operational")}</Badge>}
                </div>
              </div>
            </div>
          );
        }

        return (
          <AccordionItem key={node.id} value={String(node.id)} className="rounded-lg border last:border-b-2">
            <AccordionTrigger className="px-3 py-2">
              <div className="flex w-full items-start justify-between gap-2">
                <div className="flex-1 text-left">
                  <div className="text-sm font-medium text-foreground">
                    {node.name}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {`${node.shortName ?? ""} • ${node.province ?? ""}`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {Boolean(node.isOperational) && <Badge>{t("adminorganization.operational")}</Badge>}
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-3">
              <HierarchyAccordion nodes={children} t={t} />
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}
